import type {
  ConversationState,
  ConversationListResponse,
  ConversationMessagesResponse,
  ConversationToolCallsResponse,
  ConversationMetrics,
  ToolCallMetrics,
  ConversationFilter,
  WebSocketMessage,
} from '../types/conversation';

// Import the auth store to get the current session
import { useAuthStore } from '../stores/auth';

// Utility function to get session from multiple sources
function getSessionFromAnySource() {
  try {
    // First try to get from auth store
    const authStore = useAuthStore();
    if (authStore.user?.sessionId) {
      console.log('🔍 getSessionFromAnySource - Found session in auth store');
      return authStore.user;
    }
    
    // Fallback to localStorage
    const sessionData = localStorage.getItem('mcp_session');
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        // Handle both wrapped format (from storage service) and unwrapped format (from auth-api)
        if (parsed && typeof parsed === 'object' && 'data' in parsed) {
          // Wrapped format: { data: {...}, timestamp: ..., version: ... }
          console.log('🔍 getSessionFromAnySource - Found wrapped session in localStorage');
          return parsed.data;
        } else {
          // Unwrapped format: direct session object
          console.log('🔍 getSessionFromAnySource - Found direct session in localStorage');
          return parsed;
        }
      } catch (parseError) {
        console.error('Error parsing session data from localStorage:', parseError);
      }
    }
    
    console.log('🔍 getSessionFromAnySource - No session found in any source');
    return null;
  } catch (error) {
    console.error('Error getting session from any source:', error);
    return null;
  }
}

export class ConversationService {
  private baseUrl: string;
  private ws: WebSocket | null = null;
  private wsCallbacks: Map<string, (message: WebSocketMessage) => void> = new Map();

  constructor(baseUrl = '') {
    // For development, use relative URLs to work with Vite proxy
    // For production, use the provided baseUrl or environment variable
    if (import.meta.env.DEV) {
      this.baseUrl = '';
    } else {
      this.baseUrl = baseUrl || import.meta.env.VITE_API_BASE_URL || window.location.origin;
    }
    console.log('🔍 ConversationService - Base URL set to:', this.baseUrl);
    
    // Debug: Check session on initialization
    this.debugSession();
  }

  private debugSession(): void {
    try {
      console.log('🔍 ConversationService - Constructor debug - Checking session sources');
      
      // Get session from multiple sources
      const session = getSessionFromAnySource();
      
      console.log('🔍 ConversationService - Constructor debug - Session from utility function:', session);
      console.log('🔍 ConversationService - Constructor debug - Session sessionId:', session?.sessionId);
      
      if (session) {
        console.log('🔍 ConversationService - Constructor debug - Session data:', session);
      } else {
        console.log('🔍 ConversationService - Constructor debug - No session found in any source');
      }
    } catch (error) {
      console.error('🔍 ConversationService - Constructor debug - Error checking session:', error);
    }
  }

  // API Methods
  async getConversations(
    limit = 50,
    offset = 0,
    filter?: ConversationFilter
  ): Promise<ConversationListResponse> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });

    if (filter) {
      if (filter.isActive !== undefined) {
        params.append('isActive', filter.isActive.toString());
      }
      if (filter.userId) {
        params.append('userId', filter.userId.toString());
      }
      if (filter.dateFrom) {
        params.append('dateFrom', filter.dateFrom);
      }
      if (filter.dateTo) {
        params.append('dateTo', filter.dateTo);
      }
      if (filter.hasToolCalls !== undefined) {
        params.append('hasToolCalls', filter.hasToolCalls.toString());
      }
      if (filter.minMessages) {
        params.append('minMessages', filter.minMessages.toString());
      }
      if (filter.maxMessages) {
        params.append('maxMessages', filter.maxMessages.toString());
      }
      if (filter.searchTerm) {
        params.append('searchTerm', filter.searchTerm);
      }
    }

    const headers = this.getAuthHeaders();
    console.log('🔍 ConversationService - getConversations - Request details:', {
      url: `${this.baseUrl}/api/conversations?${params}`,
      headers: headers,
      method: 'GET'
    });

    const response = await fetch(`${this.baseUrl}/api/conversations?${params}`, {
      headers: headers,
    });

    console.log('🔍 ConversationService - getConversations - Response status:', response.status);
    console.log('🔍 ConversationService - getConversations - Response headers:', Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      throw new Error(`Failed to fetch conversations: ${response.statusText}`);
    }

    return response.json();
  }

  async getConversation(chatId: string): Promise<ConversationState> {
    const response = await fetch(`${this.baseUrl}/api/conversations/${chatId}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch conversation: ${response.statusText}`);
    }

    return response.json();
  }

  async getConversationMessages(
    chatId: string,
    limit = 100,
    offset = 0
  ): Promise<ConversationMessagesResponse> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });

    const response = await fetch(`${this.baseUrl}/api/conversations/${chatId}/messages?${params}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch conversation messages: ${response.statusText}`);
    }

    return response.json();
  }

  async getConversationToolCalls(chatId: string): Promise<ConversationToolCallsResponse> {
    const response = await fetch(`${this.baseUrl}/api/conversations/${chatId}/tools`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch conversation tool calls: ${response.statusText}`);
    }

    return response.json();
  }

  async deleteConversation(chatId: string): Promise<{ success: boolean; chatId: string }> {
    const response = await fetch(`${this.baseUrl}/api/conversations/${chatId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to delete conversation: ${response.statusText}`);
    }

    return response.json();
  }

  async getMetrics(): Promise<{
    conversations: ConversationMetrics | null;
    tools: ToolCallMetrics | null;
    timestamp: string;
  }> {
    const headers = this.getAuthHeaders();
    console.log('🔍 ConversationService - getMetrics - Request details:', {
      url: `${this.baseUrl}/api/conversations/metrics`,
      headers: headers,
      method: 'GET'
    });

    const response = await fetch(`${this.baseUrl}/api/conversations/metrics`, {
      headers: headers,
    });

    console.log('🔍 ConversationService - getMetrics - Response status:', response.status);
    console.log('🔍 ConversationService - getMetrics - Response headers:', Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      throw new Error(`Failed to fetch metrics: ${response.statusText}`);
    }

    return response.json();
  }

  async cleanupConversations(): Promise<{
    success: boolean;
    cleanedCount: number;
    timestamp: string;
  }> {
    const response = await fetch(`${this.baseUrl}/api/conversations/cleanup`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to cleanup conversations: ${response.statusText}`);
    }

    return response.json();
  }

  // WebSocket Methods
  connectWebSocket(
    onMessage?: (message: WebSocketMessage) => void,
    onConnect?: () => void,
    onDisconnect?: () => void,
    onError?: (error: Event) => void
  ): void {
    if (this.ws) {
      this.disconnectWebSocket();
    }

    // Get authentication token for WebSocket connection
    let wsUrl = '';
    
    if (import.meta.env.DEV) {
      // In development, use relative WebSocket URL (will be handled by Vite proxy)
      wsUrl = `ws://localhost:3000/api/conversations/ws`;
    } else {
      // In production, use the full WebSocket URL
      wsUrl = `${this.baseUrl.replace('http', 'ws')}/api/conversations/ws`;
    }
    
    try {
      // Get session from multiple sources
      const session = getSessionFromAnySource();
      
      if (session?.sessionId) {
        wsUrl += `?token=${encodeURIComponent(session.sessionId)}`;
      }
    } catch (error) {
      console.error('Error getting session for WebSocket:', error);
    }

    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      console.log('WebSocket connected to conversation updates');
      if (onConnect) onConnect();
    };

    this.ws.onmessage = event => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);

        // Handle specific message types
        if (message.type === 'ping') {
          this.sendWebSocketMessage({ type: 'pong', timestamp: new Date().toISOString() });
          return;
        }

        // Call registered callbacks
        for (const callback of this.wsCallbacks.values()) {
          callback(message);
        }

        // Call main message handler
        if (onMessage) onMessage(message);
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    this.ws.onclose = () => {
      console.log('WebSocket disconnected from conversation updates');
      this.ws = null;
      if (onDisconnect) onDisconnect();
    };

    this.ws.onerror = error => {
      console.error('WebSocket error:', error);
      if (onError) onError(error);
    };
  }

  disconnectWebSocket(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
      this.wsCallbacks.clear();
    }
  }

  subscribeToConversation(chatId: string): void {
    this.sendWebSocketMessage({
      type: 'subscribe',
      data: { chatId },
      timestamp: new Date().toISOString(),
    });
  }

  subscribeToUser(userId: number): void {
    this.sendWebSocketMessage({
      type: 'subscribe',
      data: { userId },
      timestamp: new Date().toISOString(),
    });
  }

  subscribeToAll(): void {
    this.sendWebSocketMessage({
      type: 'subscribe',
      data: { type: 'all' },
      timestamp: new Date().toISOString(),
    });
  }

  unsubscribeFromConversation(chatId: string): void {
    this.sendWebSocketMessage({
      type: 'unsubscribe',
      data: { chatId },
      timestamp: new Date().toISOString(),
    });
  }

  unsubscribeFromUser(userId: number): void {
    this.sendWebSocketMessage({
      type: 'unsubscribe',
      data: { userId },
      timestamp: new Date().toISOString(),
    });
  }

  unsubscribeFromAll(): void {
    this.sendWebSocketMessage({
      type: 'unsubscribe',
      data: { type: 'all' },
      timestamp: new Date().toISOString(),
    });
  }

  registerWebSocketCallback(id: string, callback: (message: WebSocketMessage) => void): void {
    this.wsCallbacks.set(id, callback);
  }

  unregisterWebSocketCallback(id: string): void {
    this.wsCallbacks.delete(id);
  }

  private sendWebSocketMessage(message: WebSocketMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not connected');
    }
  }

  private getAuthHeaders(): HeadersInit {
    try {
      console.log('🔍 ConversationService - getAuthHeaders called');
      
      // Get session from multiple sources
      const session = getSessionFromAnySource();
      
      console.log('🔍 ConversationService - Session from utility function:', session);
      console.log('🔍 ConversationService - Session sessionId:', session?.sessionId);
      console.log('🔍 ConversationService - Session type:', typeof session?.sessionId);
      console.log('🔍 ConversationService - Session length:', session?.sessionId?.length);

      const headers = {
        'Content-Type': 'application/json',
        ...(session?.sessionId && { Authorization: `Bearer ${session.sessionId}` }),
      };

      console.log('🔍 ConversationService - Final headers:', headers);
      console.log('🔍 ConversationService - Has Authorization header:', !!headers.Authorization);
      
      return headers;
    } catch (error) {
      console.error('Error getting auth headers:', error);
      return {
        'Content-Type': 'application/json',
      };
    }
  }

  // Utility methods
  isWebSocketConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  formatDuration(minutes: number): string {
    if (minutes < 60) {
      return `${minutes}m`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours < 24) {
      return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
    }

    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;

    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  }

  formatTimestamp(timestamp: string): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) {
      return 'Just now';
    } else if (diffMinutes < 60) {
      return `${diffMinutes}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  }

  exportConversation(conversation: ConversationState): string {
    const exportData = {
      ...conversation,
      exportedAt: new Date().toISOString(),
      version: '1.0.0',
    };

    return JSON.stringify(exportData, null, 2);
  }

  downloadConversationAsJson(conversation: ConversationState): void {
    const exportedData = this.exportConversation(conversation);
    const blob = new Blob([exportedData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `conversation_${conversation.chatId}_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

// Create a singleton instance
export const conversationService = new ConversationService();
